import { useState } from "react";
import { useStore } from "../lib/storeContext";
import { saveIdea, saveIdeaFromUrl } from "../lib/mutations";
import type { AssetRef } from "../lib/types";

/**
 * Heart toggle that saves a render to the room's Ideabook (idempotent by source).
 * Works for both synced renders (renderFileName + renderAsset) and job-fallback
 * renders (renderUrl + sourceId, which are downloaded + uploaded on save).
 */
export function SaveHeart({
  roomId,
  goal,
  renderFileName,
  renderAsset,
  renderUrl,
  sourceId,
  className,
}: {
  roomId: string;
  goal: string;
  renderFileName?: string;
  renderAsset?: AssetRef;
  renderUrl?: string;
  sourceId?: string;
  className?: string;
}) {
  const { store, touch } = useStore();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dedupeKey = renderFileName ?? sourceId ?? "";
  const saved = !!dedupeKey && store.ideaSourceFileNames(roomId).has(dedupeKey);

  // Viewers can't write IDEAs to a shared project — hide the heart entirely.
  if (store.roleFor(store.projectIdOf("ROOM", roomId)) === "viewer") return null;

  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (saved || busy) return;
    setBusy(true);
    setErr(null);
    try {
      if (renderUrl && sourceId) {
        await saveIdeaFromUrl(store, roomId, { url: renderUrl, sourceId, goal });
      } else if (renderFileName && renderAsset) {
        await saveIdea(store, roomId, { renderFileName, renderAsset, goal });
      }
      touch();
    } catch (e) {
      // e.g. the server rejected the write (role revoked mid-session).
      setErr(e instanceof Error ? e.message : "Couldn't save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      onClick={onClick}
      disabled={busy}
      title={err ?? (saved ? "Saved to Ideabook" : "Save to Ideabook")}
      className={`flex h-8 w-8 items-center justify-center rounded-full text-lg shadow-card backdrop-blur transition ${
        saved ? "bg-white/90 text-accent" : "bg-black/40 text-white hover:bg-black/60"
      } ${className ?? ""}`}
    >
      {err ? "!" : saved ? "♥" : "♡"}
    </button>
  );
}
